import {
  MdBookOnline,
  MdEdit,
  MdDelete,
  MdVisibility,
  MdPerson,
  MdFlight,
  MdBusiness,
  MdAttachMoney,
  MdSchedule,
  MdCheckCircle,
  MdCancel,
  MdPayment,
} from 'react-icons/md'
import { Badge } from '../../components/ui'

const BookingList = ({ bookings, onViewBooking, onEditBooking, onDeleteBooking }) => {
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success'
      case 'pending':
        return 'warning'
      case 'cancelled':
        return 'error'
      case 'completed':
        return 'info'
      default:
        return 'neutral'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <MdCheckCircle className="text-success-600 dark:text-success-400" />
      case 'pending':
        return <MdSchedule className="text-warning-600 dark:text-warning-400" />
      case 'cancelled':
        return <MdCancel className="text-error-600 dark:text-error-400" />
      case 'completed':
        return <MdCheckCircle className="text-info-600 dark:text-info-400" />
      default:
        return <MdBookOnline className="text-neutral-600 dark:text-neutral-400" />
    }
  }

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success'
      case 'pending':
        return 'warning'
      case 'failed':
        return 'error'
      case 'refunded':
        return 'info'
      default:
        return 'neutral'
    }
  }

  return (
    <div className="space-y-4">
      {bookings.map((booking) => (
        <div
          key={booking._id}
          className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Booking Basic Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                      Booking #{booking?.bookingNumber?.slice(4) || '-'}
                    </h3>
                    <Badge variant={getStatusColor(booking.status)}>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(booking.status)}
                        <span className="capitalize">{booking.status}</span>
                      </div>
                    </Badge>
                    <Badge variant={getPaymentStatusColor(booking.paymentStatus)}>
                      <div className="flex items-center gap-1">
                        <MdPayment className="text-sm" />
                        <span className="capitalize">{booking.paymentStatus}</span>
                      </div>
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-600 dark:text-neutral-400">
                    <div className="flex items-center gap-1">
                      <MdFlight className="text-lg" />
                      <span>{booking.flight?.number}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MdBusiness className="text-lg" />
                      <span>{booking.flight?.airline?.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MdPerson className="text-lg" />
                      <span>{booking.passengers?.length || 0} passenger(s)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MdAttachMoney className="text-lg" />
                      <span>{formatCurrency(booking.price, booking.paymentCurrency)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Flight Details
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>{booking.flight?.number}</div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {booking.flight?.departure ? formatDateTime(booking.flight.departure) : 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Passengers
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>{booking.passengers?.length || 0} passenger(s)</div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {booking.passengers?.map((p) => p.seat).join(', ') || 'No seats assigned'}
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Payment
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>{formatCurrency(booking.price, booking.paymentCurrency)}</div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {booking.paymentMethod?.replace('_', ' ')}
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Created
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>{formatDate(booking.createdAt)}</div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {formatDate(booking.updatedAt)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-2">
                <button
                  onClick={() => onViewBooking(booking)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors duration-200"
                >
                  <MdVisibility className="text-sm" />
                  View
                </button>
                <button
                  onClick={() => onEditBooking(booking)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-warning-600 dark:text-warning-400 hover:text-warning-700 dark:hover:text-warning-300 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded-lg transition-colors duration-200"
                >
                  <MdEdit className="text-sm" />
                  Edit
                </button>
                <button
                  onClick={() => onDeleteBooking(booking)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors duration-200"
                >
                  <MdDelete className="text-sm" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default BookingList
